import { useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import ProviderBioModal from './ProviderBioModal';

/**
 * Chip button that triggers a modal showing the provider's bio.
 * Used exclusively in Business Mode (businessName ≠ providerName).
 *
 * Renders a pill-shaped button: optional 40px circular avatar +
 * "Meet {providerName}" text + right-chevron. Clicking opens the shared
 * Modal with the provider's full bio content.
 *
 * The chip exists to surface the bio, so it requires both a provider name
 * AND a configured bio. Returns null when either is absent or blank —
 * without a bio there is nothing meaningful to surface beyond the name and
 * photo already shown elsewhere.
 *
 * @param {Object} bookingPage - Booking page data object.
 */
const ProviderBioTrigger = ({ bookingPage }) => {
  const [open, setOpen] = useState(false);

  const providerName = bookingPage?.providerName?.trim() || null;
  const providerBio = bookingPage?.providerBio?.trim() || null;
  const profilePhotoUrl = bookingPage?.profilePhotoUrl || null;

  // The chip exists to surface the bio — suppress it when there's no
  // provider name or no bio to show.
  if (!providerName || !providerBio) {
    return null;
  }

  return (
    <>
      {/* Chip trigger */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="inline-flex items-center gap-2.5 rounded-full border border-themed bg-themed-card px-4 py-2 text-sm font-medium text-themed-primary shadow-sm transition-colors hover:bg-themed-card focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-primary)] focus-visible:ring-offset-2 min-w-0 max-w-full"
      >
        {profilePhotoUrl && (
          <img
            src={profilePhotoUrl}
            alt=""
            className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
          />
        )}
        <span className="min-w-0 break-words">Meet {providerName}</span>
        <ChevronRightIcon className="h-4 w-4 flex-shrink-0 text-themed-secondary" aria-hidden="true" />
      </button>

      {/* Bio modal — body lives in the shared ProviderBioModal */}
      <ProviderBioModal
        open={open}
        onClose={() => setOpen(false)}
        bookingPage={bookingPage}
      />
    </>
  );
};

export default ProviderBioTrigger;
